import { Province } from '@/constructs/provinces/Province';
import { subform } from '@/react/view-models/Form/subform';
import {
  isState,
  maxLength,
  minLength,
  nonEmpty,
  onlyLetters,
  onlyNumeric,
  validAddressLine
} from '@/react/view-models/Form/validation-utils';
import { Country, msg } from '@/services/isomorphic/I18NService';
import { IAddress } from '@/services/models/Address';
import { OrderPhysicalShippingInfoModel } from '@/services/models/Order';
import { DTO } from '@/type-utils';
import { computed, makeObservable, observable } from 'mobx';
import { FormModel, validatable } from '../../../Form';
import { IClassicShippingForm } from './IClassicShippingForm';
import { NamesFormModel } from './NamesFormModel';
import { ContactInfoFormModel } from './ContactInfoFormModel';
import { forms_field_useShippingAddressForBilling } from "@/lang/__generated__/ahnu/forms_field_useShippingAddressForBilling";
import { forms_field_sendShippingNotification } from "@/lang/__generated__/ahnu/forms_field_sendShippingNotification";
import { forms_field_shippingSelection } from "@/lang/__generated__/ahnu/forms_field_shippingSelection";
import { forms_address_line1 } from "@/lang/__generated__/ahnu/forms_address_line1";
import { forms_address_line2 } from "@/lang/__generated__/ahnu/forms_address_line2";
import { forms_address_zipCode } from "@/lang/__generated__/ahnu/forms_address_zipCode";
import { forms_address_city } from "@/lang/__generated__/ahnu/forms_address_city";
import { forms_address_state } from "@/lang/__generated__/ahnu/forms_address_state";
import { forms_address_country } from "@/lang/__generated__/ahnu/forms_address_country";
import { forms_field_saveToAddressBook } from "@/lang/__generated__/ahnu/forms_field_saveToAddressBook";

/** @inheritdoc */
export class ShippingFormModel
  extends FormModel<IClassicShippingForm>
  implements IClassicShippingForm
{
  /** @inheritdoc */
  @subform()
  public names: NamesFormModel;

  /** @inheritdoc */
  @subform()
  public contactInfo: ContactInfoFormModel;

  /** @inheritdoc */
  @observable
  @validatable({
    get name() {
      return msg(forms_field_useShippingAddressForBilling);
    },
    validations: []
  })
  public useShippingAddressAsBillingAddress: boolean;

  /** @inheritdoc */
  @observable
  @validatable({
    get name() {
      return msg(forms_field_sendShippingNotification);
    },
    validations: []
  })
  public sendShippingNotification: boolean;

  /** @inheritdoc */
  @observable
  @validatable({
    get name() {
      return msg(forms_field_shippingSelection);
    },
    validations: [nonEmpty]
  })
  public shippingMethod: string;

  /** @inheritdoc */
  @observable
  public selectedSavedAddressId: string;

  /** Address Fields. */

  /** @inheritdoc */
  @observable
  @validatable({
    get name() {
      return msg(forms_address_line1);
    },
    validations: [nonEmpty, minLength(1), validAddressLine]
  })
  public addressLine1: string;

  /** @inheritdoc */
  @observable
  @validatable({
    get name() {
      return msg(forms_address_line2);
    },
    validations: [validAddressLine]
  })
  public addressLine2: string;

  /** @inheritdoc */
  @observable
  @validatable({
    get name() {
      return msg(forms_address_zipCode);
    },
    validations: [nonEmpty, minLength(5), maxLength(5), onlyNumeric]
  })
  public zipPostalCode: string;

  /** @inheritdoc */
  @observable
  @validatable({
    get name() {
      return msg(forms_address_city);
    },
    validations: [nonEmpty, onlyLetters, minLength(1)]
  })
  public city: string;

  /** @inheritdoc */
  @observable
  @validatable({
    get name() {
      return msg(forms_address_state);
    },
    validations: [isState]
  })
  // Allow an empty string to require the user to explicitly select a state
  // (since "" wouldn't pass the `isState` validation)
  public stateProvince: Province | '';

  /** @inheritdoc */
  @observable
  @validatable({
    get name() {
      return msg(forms_address_country);
    },
    validations: [nonEmpty]
  })
  public country: Country;

  /** @inheritdoc */
  @observable
  @validatable({
    get name() {
      return msg(forms_field_saveToAddressBook);
    },
    validations: []
  })
  public shouldSaveAddress: boolean;

  /** @inheritdoc */
  public constructor(dto: DTO<IClassicShippingForm>) {
    super(dto);

    this.names = NamesFormModel.from(dto.names);
    this.contactInfo = ContactInfoFormModel.from(dto.contactInfo);

    this.useShippingAddressAsBillingAddress =
      dto.useShippingAddressAsBillingAddress;
    this.sendShippingNotification = dto.sendShippingNotification;
    this.shippingMethod = dto.shippingMethod;
    this.selectedSavedAddressId = dto.selectedSavedAddressId;
    this.shouldSaveAddress = dto.shouldSaveAddress;

    /** Address Fields. */
    this.addressLine1 = dto.addressLine1;
    this.addressLine2 = dto.addressLine2 ?? '';
    this.zipPostalCode = dto.zipPostalCode;
    this.city = dto.city;
    this.stateProvince = dto.stateProvince;
    this.country = dto.country;

    makeObservable(this);
  }

  /** @inheritdoc */
  @computed
  public get address(): IAddress {
    const addressLine2 =
      this.addressLine2.length === 0 ? undefined : this.addressLine2;

    const { firstName, lastName } = this.names;

    return {
      addressLine1: this.addressLine1,
      addressLine2,
      zipPostalCode: this.zipPostalCode,
      city: this.city,
      stateProvince: this.stateProvince as Province,
      country: this.country,

      firstName,
      lastName
    };
  }

  /**
   * Creates an {@link OrderPhysicalShippingInfoModel} object from this form. This object can then
   * be consumed by the order placement methods.
   *
   * @returns An {@link OrderPhysicalShippingInfoModel} object with the relevant data this form
   * contains.
   */
  public toOrderShippingInfo(): OrderPhysicalShippingInfoModel {
    const {
      address,
      contactInfo: { email, phoneNumber },
      shippingMethod,
      shouldSaveAddress
    } = this;

    return new OrderPhysicalShippingInfoModel({
      email,
      phoneNumber,
      shippingMethod,
      address,
      shouldSaveAddress
    });
  }

  /**
   * Clears the address fields.
   */
  public clearAddress(): void {
    this.names.firstName = '';
    this.names.lastName = '';
    this.addressLine1 = '';
    this.addressLine2 = '';
    this.city = '';
    this.stateProvince = '';
    this.zipPostalCode = '';
  }

  /**
   * Sets the address fields.
   *
   * @param address - The address to set.
   */
  public setAddress(address: IAddress): void {
    this.names.firstName = address.firstName;
    this.names.lastName = address.lastName;
    this.addressLine1 = address.addressLine1;
    this.addressLine2 = address.addressLine2 ?? '';
    this.city = address.city;
    this.stateProvince = address.stateProvince;
    this.zipPostalCode = address.zipPostalCode;
    this.country = address.country;
  }

  /** @inheritdoc */
  public toDTO(): DTO<IClassicShippingForm> {
    return {
      names: this.names.toDTO(),
      contactInfo: this.contactInfo.toDTO(),
      useShippingAddressAsBillingAddress:
        this.useShippingAddressAsBillingAddress,
      sendShippingNotification: this.sendShippingNotification,
      shippingMethod: this.shippingMethod,
      selectedSavedAddressId: this.selectedSavedAddressId,
      shouldSaveAddress: this.shouldSaveAddress,
      // Address Fields
      addressLine1: this.addressLine1,
      addressLine2: this.addressLine2 ?? '',
      zipPostalCode: this.zipPostalCode,
      city: this.city,
      stateProvince: this.stateProvince,
      country: this.country
    };
  }
}
